import React, { useMemo } from 'react'
import { useDispatch, AppDispatch } from 'src/state/redux'
import im from 'immer'
import { DNAIconStyleProps, DNATextProps, GenericToast, ToastOrientations, useToast } from '@alucio/lux-ui'
import { Dispatch } from 'redux'
import { AssociatedFileORM, DocumentORM, DocumentVersionORM, EmailTemplateORM } from 'src/types/types'
import { CurrentUser, useCurrentUser } from 'src/state/redux/selector/user'
import { ANALYTIC_CONTEXT } from 'src/state/machines/analytics/analytics.types';
import { useRouteMatch, match } from 'src/router'

import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal'
import { contentPreviewModalActions } from 'src/state/redux/slice/contentPreviewModal'

import { handleBookmark } from 'src/components/Library/Util/util'
import { downloadContentFromCloudfront } from 'src/utils/loadCloudfrontAsset/loadCloudfrontAsset'
import DNADocumentStatusChangeModal from 'src/components/DNA/Modal/DNADocumentStatusChange'

import DNADocumentAddToFolder from 'src/components/DNA/Modal/DNADocumentAddToFolder'
import DNAFileRenameModal from '../Modal/DNAFileRenameModal'
import { ToastActions } from '@alucio/lux-ui/lib/components/Toast/useToast'
import { ContentShareModal, ShareVariantOptions } from '../Modal/ContentShareModal/ContentShareModal'
import { useEmailTemplateListForMSL } from 'src/state/redux/selector/emailTemplate'
import { sortCollection } from 'src/state/redux/selector/common'
import { useTenantCustomFields } from 'src/state/redux/selector/tenant'
import emailTemplateQuery from 'src/state/redux/emailTemplate/query'

import { generateClient } from 'aws-amplify/api';
import {
  downloadDocumentVersionAsPdfFile,
} from '@alucio/aws-beacon-amplify/src/graphql/mutations'
import {
  CustomFieldDefinition,
  CustomFieldUsage,
  DocumentAccessLevel,
  FileType,
} from '@alucio/aws-beacon-amplify/src/models'
import { userActions } from 'src/state/redux/slice/user'
import { downloadAsLink } from 'src/screens/Documents/Export'
import { useAppSettings } from 'src/state/context/AppSettings'
import omit from 'lodash/omit'
import { copyDocPageUrlHandler } from 'src/utils/shareLink/shareLink.web'
import useCurrentPage from '../hooks/useCurrentPage'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags'
import * as logger from 'src/utils/logger'
import { DownloadDocumentVersionAsPdfFileMutation } from '@alucio/aws-beacon-amplify/src/API'

/**
 * [TODO] - Create safer structure/typings with Folder equivalent
 */

export type MatchFolderParams = {
  page: string,
  folderId: string
}

export type MatchNestedFolderParams = MatchFolderParams & {
  nestedFolderId: string
}

// [TODO-2126]  - Currently we use onCallback to allow any action to have allow deferred execution
//                for actions. (i.e. First dismiss a prompt THEN execute an action in here)
//              - ActionCallbacks are non-deferred but accessible to the action
//              - Consider combining onCallback into ActionsCallback, where we may accept both
//                deferred and non-deferred in one argument (instead of two separate ones)
export type ActionCallbacks = Partial<Record<DocumentContextAction, (documentORM: DocumentORM) => void>>

// [TODO]
//  - Can probably refactor this into action specific arguments
//    That is, arguments are starting to get crazy and aren't need for all actions
export type DocumentContextOptionsWithoutBulkActions = {
  [key in DocumentContextAction]: {
    icon: string,
    title: string,
    popoverText?: string,
    status?: DNATextProps['status'],
    iconStatus?: DNAIconStyleProps['status'],
    onPress: (
      d: DocumentORM,
      dispatch: AppDispatch,
      // [TODO] - Not the cleanest, starting to deviate ...
      user: CurrentUser,
      match: {
        folder: match<MatchFolderParams> | null
        nestedFolder: match<MatchNestedFolderParams> | null
      },
      toast: ToastActions,
      isOnline?: boolean,
      emailTemplates?: EmailTemplateORM[],
      onCallback?: (cb: () => void) => void,
      actionCallback?: (docORM: DocumentORM) => void,
      customFields?: CustomFieldDefinition[],
      downloadBeaconFile?: boolean,
      pageNumber?: number,
      context?: ANALYTIC_CONTEXT,
    ) => () => void
  }
}

type BulkShareAndCopyOnPress = {
  onPress: (
    d: DocumentORM[],
    dispatch: AppDispatch,
    // [TODO] - Not the cleanest, starting to deviate ...
    user: CurrentUser,
    match: {
      folder: match<MatchFolderParams> | null
      nestedFolder: match<MatchNestedFolderParams> | null
    },
    toast: ToastActions,
    isOnline?: boolean,
    emailTemplates?: EmailTemplateORM[],
    onCallback?: (cb: () => void) => void,
    actionCallback?: (docORM: DocumentORM) => void,
    customFields?: CustomFieldDefinition[],
    downloadBeaconFile?: boolean,
    pageNumber?: number,
    context?: ANALYTIC_CONTEXT,
  ) => () => void
}

interface DocumentContextOptions extends DocumentContextOptionsWithoutBulkActions {
  bulkAddToFolder: {
    onPress: (
      d: DocumentORM[],
      dispatch: AppDispatch,
      // [TODO] - Not the cleanest, starting to deviate ...
      user: CurrentUser,
      match: {
        folder: match<MatchFolderParams> | null
        nestedFolder: match<MatchNestedFolderParams> | null
      },
      toast: ToastActions,
      isOnline?: boolean,
      emailTemplates?: EmailTemplateORM[],
      onCallback?: (cb: () => void) => void,
      actionCallback?: (docORM: DocumentORM) => void,
      customFields?: CustomFieldDefinition[],
      downloadBeaconFile?: boolean,
      pageNumber?: number,
      context?: ANALYTIC_CONTEXT,
    ) => () => void,
    status?: DNAIconStyleProps['status']
  },
  bulkShareEmail: BulkShareAndCopyOnPress,
  bulkCopyShareLink: BulkShareAndCopyOnPress,
  bulkDownload: BulkShareAndCopyOnPress,
  bulkFavorite: {
    onPress: (
      d: DocumentORM[],
      dispatch: AppDispatch,
      user: CurrentUser,
      match: {
        folder: match<MatchFolderParams> | null
        nestedFolder: match<MatchNestedFolderParams> | null
      },
      toast: ToastActions,
      isOnline?: boolean,
      emailTemplates?: EmailTemplateORM[],
      onCallback?: (cb: () => void) => void,
      actionCallback?: (docORM: DocumentORM) => void,
      customFields?: CustomFieldDefinition[],
      downloadBeaconFile?: boolean,
      pageNumber?: number,
      context?: ANALYTIC_CONTEXT,
    ) => () => void,
  },
}

type BindDocumentContextActionsWithoutBulkActions = {
  [key in DocumentContextAction]: (d: DocumentORM, pageNumber?: number, context?: ANALYTIC_CONTEXT) => () => void
}

export interface BindDocumentContextActions extends BindDocumentContextActionsWithoutBulkActions {
  bulkAddToFolder: (d: DocumentORM[], pageNumber?: number, context?: ANALYTIC_CONTEXT) => () => void
  bulkShareEmail: (d: DocumentORM[], pageNumber?: number, context?: ANALYTIC_CONTEXT) => () => void
  bulkCopyShareLink: (d: DocumentORM[], pageNumber?: number, context?: ANALYTIC_CONTEXT) => () => void
  bulkDownload: (d: DocumentORM[], pageNumber?: number, context?: ANALYTIC_CONTEXT) => () => void
  bulkFavorite: (d: DocumentORM[], pageNumber?: number, context?: ANALYTIC_CONTEXT) => () => void
}

export enum DocumentContextActions {
  addToFolder = 'Add to folder',
  rename = 'Rename',
  duplicate = 'Duplicate',
  bookmark = 'Add to favorites',
  unbookmark = 'Remove from favorites',
  beaconLink = 'Copy link',
  publish = 'Publish',
  archive = 'Archive',
  unarchive = 'Unarchive',
  delete = 'Delete',
  downloadAsPDF = 'Download as PDF',
  shareEmail = 'Share',
  revoke = 'Revoke',
  present = 'View',
  preview = 'View',
  publisherPreview = 'Publisher Preview',
  download = 'Download',
  version = 'Version',
  unrevoke = 'Unrevoke',
}

/**
 * Get the file type from the documentORM or an array of documentORMs
 * @param doc - The documentORM or an array of documentORMs
 * @returns The file type
 */
const getFileType = (doc: DocumentORM | DocumentORM[]): FileType | undefined => {
  if (Array.isArray(doc)) {
    return doc.length > 0 ? doc[0]?.model?.type as FileType : undefined
  }
  return doc?.model?.type as FileType
}

/**
 * Checks if a document should be forced to download as PDF
 * @param action - The action being performed
 * @param fileType - The type of file
 * @param forceDownloadAsPDF - Flag to force PDF download
 */
const shouldForceDownloadAsPDF = (
  action: string,
  fileType?: FileType,
  forceDownloadAsPDF?: boolean,
): boolean => {
  const isPPTXFile = fileType !== undefined && fileType === FileType.PPTX;
  const isDownloadAction = action === 'download';

  return Boolean(forceDownloadAsPDF && isDownloadAction && isPPTXFile);
}

/**
 * takes the original document and remove the circular dependencies
 *  IMPORTANT: this document is intended for read only purposes and should not be used to update the original document
 * @param documentVersion
 * @returns  cloned objec
 */
const removeCircularDependencies = (documentVersion: DocumentVersionORM) => {
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Cyclic_object_value
  const getCircularReplacer = () => {
    const seen = new WeakSet();
    return (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return;
        }
        seen.add(value);
      }
      return value;
    };
  };

  const cloneDocVersion = (docVersion: DocumentVersionORM): DocumentVersionORM => {
    const cloned = JSON.parse(JSON.stringify(docVersion, getCircularReplacer()))

    return {
      ...cloned,
      model: docVersion.model,
      meta: docVersion.meta,
      relations: {
        ...cloned.relations,
        pageGroups: docVersion.relations.pageGroups,
        pages: docVersion.relations.pages.map(page => omit(page, 'relations')),
        associatedFiles: docVersion.relations.associatedFiles,
      },
    }
  }

  return cloneDocVersion(documentVersion)
}

export type DocumentContextAction = keyof typeof DocumentContextActions

/** [TODO] WE CAN PROBABLY CONSOLIDATE BOTH FOLDER AND DOCUMENT */
/** This is a bit more convenient than using the raw contextActions below */
export const useDNADocumentActions = (
  onCallback?: (cb: () => void) => void,
  actionCallbacks?: ActionCallbacks,
): BindDocumentContextActions => {
  const dispatch = useDispatch()
  const currentUser = useCurrentUser()
  const folder = useRouteMatch<MatchFolderParams>('/:page/:folderId')
  const nestedFolder = useRouteMatch<MatchNestedFolderParams>('/:page/:folderId/:nestedFolderId')
  const route = useCurrentPage({ exact: false })
  const isPublisher = route?.configOptions?.modules?.includes('publisher')
  const enablePowerPointAddin = useFeatureFlags('enablePowerPointAddin');
  const forceDownloadAsPDF = useFeatureFlags('BEAC_5882_MSL_Download_PPTX_as_PDF') && !isPublisher;
  const toast = useToast()
  const { isOnline } = useAppSettings()
  const emailTemplates = useEmailTemplateListForMSL()
  const customFieldsConfig = useMemo(() => ({ usages: { internalUsages: [CustomFieldUsage.CONTENT_SHARE] } }), [])
  const orderedTemplates = sortCollection(
    emailTemplates,
    emailTemplateQuery.sorts.titleAsc,
  )
  const customFieldsContentShare = useTenantCustomFields(customFieldsConfig)

  const bound = useMemo(() => im(documentContextOptions, draft => {
    for (const action in documentContextOptions) {
      draft[action] = (
        documentORM: DocumentORM | DocumentORM[],
        pageNumber?: number,
        context?: ANALYTIC_CONTEXT,
      ) => {
        /* Get the action callback and options, but override them to force PDF download
           if forceDownloadAsPDF flag is enabled and this is a download action */
        let actionCallback = actionCallbacks?.[action]
        let actionOptions = documentContextOptions[action]

        const fileType = getFileType(documentORM)
        if (shouldForceDownloadAsPDF(action, fileType, forceDownloadAsPDF)) {
          actionCallback = actionCallbacks?.downloadAsPDF
          actionOptions = documentContextOptions.downloadAsPDF
        }

        return actionOptions.onPress(
          documentORM,
          dispatch,
          currentUser,
          { folder, nestedFolder },
          toast,
          isOnline,
          orderedTemplates,
          onCallback,
          actionCallback,
          customFieldsContentShare,
          !isPublisher && enablePowerPointAddin,
          pageNumber,
          context,
        )
      }
    }
  }) as unknown as BindDocumentContextActions,
  [
    dispatch,
    currentUser,
    folder,
    nestedFolder,
    toast,
    isOnline,
    emailTemplates,
    orderedTemplates,
    customFieldsContentShare,
    onCallback,
    actionCallbacks,
    forceDownloadAsPDF,
  ],
  )
  return bound
}

export const documentContextOptions: DocumentContextOptions = {
  bulkAddToFolder: {
    onPress: (
      docORMs: DocumentORM[],
      dispatch: AppDispatch,
      _u: CurrentUser,
      _m: { folder: match<MatchFolderParams> | null; nestedFolder: match<MatchNestedFolderParams> | null },
      toast: ToastActions,
      _io?: boolean,
      _et?: EmailTemplateORM[],
      _oc?: (cb: () => void) => void,
      _ac?: (docORM: DocumentORM) => void,
      _cf?: CustomFieldDefinition[],
      _dbf?: boolean,
      _pn?: number,
      context?: ANALYTIC_CONTEXT,
    ) => () => {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: (props) => (
          <DNADocumentAddToFolder
            {...props}
            itemORM={docORMs}
            toast={toast}
            context={context}
          />
        ),
      }),
      )
    },
  },
  addToFolder: {
    icon: 'folder-simple-plus',
    title: DocumentContextActions.addToFolder,
    onPress: (
      docORM: DocumentORM,
      dispatch: Dispatch,
      _u: CurrentUser,
      _m: { folder: match<MatchFolderParams> | null; nestedFolder: match<MatchNestedFolderParams> | null },
      toast: ToastActions,
      _io?: boolean,
      _et?: EmailTemplateORM[],
      _oc?: (cb: () => void) => void,
      _ac?: (docORM: DocumentORM) => void,
      _cf?: CustomFieldDefinition[],
      _dbf?: boolean,
      _pn?: number,
      context?: ANALYTIC_CONTEXT,
    ) => () => {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: (props) => (
          <DNADocumentAddToFolder
            {...props}
            itemORM={docORM}
            toast={toast}
            context={context}
          />
        ),
      }),
      )
    },
  },
  rename: {
    icon: 'pencil-simple',
    title: DocumentContextActions.rename,
    onPress: (documentORM, dispatch) => () => {
      dispatch(DNAModalActions.setModal(
        {
          isVisible: true,
          allowBackdropCancel: false,
          component: (props) => (
            <DNAFileRenameModal
              {...props}
              documentORM={documentORM}
            />
          ),
        },
      ))
    },
  },
  duplicate: {
    icon: 'copy',
    title: DocumentContextActions.duplicate,
    onPress: () => () => {
      console.warn('Not implemented yet')
    },
  },
  bookmark: {
    icon: 'star',
    title: DocumentContextActions.bookmark,
    onPress: (
      documentORM, dispatch, user, _match, _toast, _isOnline, _emailTemplates,
      _onCallback, _actionCallback, _customFields, _downloadBeaconFile, _pageNumber, context,
    ) => () => {
      if (!user) throw new Error('Could not bookmark')

      handleBookmark(
        documentORM.model,
        user,
        dispatch,
        context,
      )
    },
  },
  unbookmark: {
    icon: 'star-fill',
    title: DocumentContextActions.unbookmark,
    onPress: () => () => {
      console.warn('Not implemented yet')
    },
  },
  beaconLink: {
    icon: 'link',
    title: DocumentContextActions.beaconLink,
    onPress: (documentORM, dispatch, _, __, toast) => () => {
      copyDocPageUrlHandler(toast, documentORM.model.id)
    },
  },
  publish: {
    icon: 'publish',
    title: DocumentContextActions.publish,
    onPress: (documentORM, dispatch, _, __, toast) => () => {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: (modalProps) => (
          <DNADocumentStatusChangeModal
            {...modalProps}
            documentORM={documentORM}
            action="PUBLISHED"
            toast={toast}
          />
        ),
      }))
    },
  },
  archive: {
    icon: 'archive',
    title: DocumentContextActions.archive,
    // eslint-disable-next-line max-len
    popoverText: 'This action will make files invisible to viewers, however publishers can still view or un-archive the file in future. Links shared with access to this file will still work until they expire.',
    onPress: (documentORM, dispatch, _, __, toast, ___, ____, onCallback) => () => {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: (modalProps) => (
          <DNADocumentStatusChangeModal
            {...modalProps}
            documentORM={documentORM}
            action={documentORM.relations.documentVersions.length > 1 ? 'ARCHIVED_WITH_VERSIONS' : 'ARCHIVED'}
            toast={toast}
            onConfirmCallback={onCallback}
          />
        ),
      }))
    },
  },
  unarchive: {
    icon: 'archive',
    title: DocumentContextActions.unarchive,
    onPress: (documentORM, dispatch, _, __, toast, ___, ____, onCallback) => () => {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: (modalProps) => (
          <DNADocumentStatusChangeModal
            {...modalProps}
            documentORM={documentORM}
            action="UNARCHIVE"
            toast={toast}
            onConfirmCallback={onCallback}
          />
        ),
      }))
    },
  },
  bulkShareEmail: {
    onPress: (
      documentORMs,
      dispatch,
    ) => () => {
      // Merge all associated files into one array
      const allAssociatedFiles: AssociatedFileORM[] = []
      documentORMs.forEach(docORM => {
        const associatedFiles = docORM.relations.version.latestUsableDocumentVersionORM?.relations.associatedFiles ?? []
        allAssociatedFiles.push(...associatedFiles)
      })

      const entityShareIds: string[] = []
      documentORMs.forEach(docORM => {
        if (docORM.relations.version.latestPublishedDocumentVersionORM) {
          entityShareIds.push(docORM.relations.version.latestPublishedDocumentVersionORM.model.id)
        }
      })

      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        backdropVisible: true,
        component: (modalProps) => (
          <ContentShareModal
            entityShareIds={entityShareIds}
            {...modalProps}
            variant={ShareVariantOptions.DOC_VER_SHARE}
          />
        ),
      }))
    },
  },
  bulkCopyShareLink: {
    onPress: (docORMs, _, __, ___, toast) => () => {
      // [TODO] Implement bulk copy share link
      if (docORMs.length > 0) {
        copyDocPageUrlHandler(toast, docORMs[0].model.id)
      }
    },
  },
  bulkDownload: {
    onPress: (docORMs) => () => {
      // [TODO] Implement bulk download
      console.warn('Bulk download not implemented yet', docORMs)
    },
  },
  bulkFavorite: {
    onPress: (
      docORMs: DocumentORM[],
      dispatch: AppDispatch,
      user: CurrentUser,
      _m: { folder: match<MatchFolderParams> | null; nestedFolder: match<MatchNestedFolderParams> | null },
      toast: ToastActions,
      _io?: boolean,
      _et?: EmailTemplateORM[],
      _oc?: (cb: () => void) => void,
      _ac?: (docORM: DocumentORM) => void,
      _cf?: CustomFieldDefinition[],
      _dbf?: boolean,
      _pn?: number,
      context?: ANALYTIC_CONTEXT,
    ) => () => {
      if (!user) throw new Error('Could not favorite')

      const { userProfile } = user
      if (!userProfile) return

      // Check if all documents are bookmarked
      const allBookmarked = docORMs.every(docORM => docORM.meta.bookmark?.isBookmarked)

      // Build new bookmarked list based on desired action
      const now = new Date().toISOString()
      const updatedBookmarks = userProfile.bookmarkedDocs ? [...userProfile.bookmarkedDocs] : []

      docORMs.forEach(docORM => {
        const docIndex = updatedBookmarks.findIndex(b => b.docID === docORM.model.id)

        if (allBookmarked && docIndex >= 0) {
          // All were bookmarked, so remove them
          updatedBookmarks.splice(docIndex, 1)
        } else if (!allBookmarked) {
          // Not all were bookmarked, so ensure ALL in selection are bookmarked with latest timestamp
          if (docIndex >= 0) {
            updatedBookmarks.splice(docIndex, 1)
          }
          updatedBookmarks.push({
            docID: docORM.model.id,
            createdAt: now,
          })
        }
      })

      analytics?.track(allBookmarked ? 'DOCUMENT_REMOVE_BOOKMARK' : 'DOCUMENT_BOOKMARK', {
        action: allBookmarked ? 'REMOVE_BOOKMARK' : 'BOOKMARK',
        category: 'DOCUMENT',
        documentIds: docORMs.map(doc => doc.model.id),
        isBulk: true,
        context,
      })

      // Single update operation to avoid race conditions
      dispatch(
        userActions.updateBookmarks({
          userId: userProfile.id,
          bookmarkedDocs: updatedBookmarks,
        }),
      )
    },
  },
  shareEmail: {
    icon: 'envelope-simple',
    title: DocumentContextActions.shareEmail,
    onPress: (
      documentORM,
      dispatch,
    ) => () => {
      const targetDocumentVersionORM = documentORM.relations.version.latestUsableDocumentVersionORM
      const entityShareIds = [targetDocumentVersionORM.model.id]

      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: false,
        backdropVisible: true,
        component: (modalProps) => (
          <ContentShareModal
            entityShareIds={entityShareIds}
            {...modalProps}
            variant={ShareVariantOptions.DOC_VER_SHARE}
          />
        ),
      }))
    },
  },
  delete: {
    icon: 'trash',
    title: DocumentContextActions.delete,
    status: 'danger',
    iconStatus: 'danger',
    // eslint-disable-next-line max-len
    popoverText: 'This action will permanently remove the file from all Beacon locations. Links shared with access to this file will no longer work.',
    onPress: (documentORM, dispatch, _, __, toast, ___, ____, onCallback) => () => {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: (modalProps) => (
          <DNADocumentStatusChangeModal
            {...modalProps}
            documentORM={documentORM}
            action={documentORM.relations.documentVersions.length > 1 ? 'DELETED_WITH_VERSIONS' : 'DELETED'}
            toast={toast}
            onConfirmCallback={onCallback}
          />
        ),
      }))
    },
  },
  revoke: {
    icon: 'arrow-u-up-left',
    title: 'Revoke',
    // eslint-disable-next-line max-len
    popoverText: 'This action will make files invisible to viewers, however publishers can still view or un-revoke the file in future. Links shared with access to this file will no longer work.',
    onPress: (documentORM, dispatch, _, __, toast, ___, ____, onCallback) => () => {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: (modalProps) => (
          <DNADocumentStatusChangeModal
            {...modalProps}
            documentORM={documentORM}
            action={documentORM.relations.documentVersions.length > 1 ? 'REVOKED_WITH_VERSIONS' : 'REVOKED'}
            toast={toast}
            onConfirmCallback={onCallback}
          />
        ),
      }))
    },
  },
  unrevoke: {
    icon: 'arrow-u-up-left',
    title: DocumentContextActions.unrevoke,
    onPress: (documentORM, dispatch, _, __, toast, ___, ____, onCallback) => () => {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: (modalProps) => (
          <DNADocumentStatusChangeModal
            {...modalProps}
            documentORM={documentORM}
            action="UNREVOKE"
            toast={toast}
            onConfirmCallback={onCallback}
          />
        ),
      }))
    },
  },
  present: {
    icon: 'eye',
    title: DocumentContextActions.present,
    onPress: (
      documentORM,
      dispatch,
      _, __, ___, ____, ______, _______,
      actionCallback,
      ___________, ____________,
      pageNumber,
    ) => () => {
      const documentVersion = documentORM.relations.version.latestUsableDocumentVersionORM

      dispatch(
        contentPreviewModalActions.setModalVisibility({
          documentVersionId: documentVersion.model.id,
          isFullWindow: true,
          isOpen: true,
          content: documentVersion,
          fromEllipsis: true,
          pageNumber,
        }),
      )
      actionCallback?.(documentORM);
    },
  },
  preview: {
    icon: 'eye',
    title: DocumentContextActions.present,
    onPress: (
      documentORM,
      dispatch, _, __,
      toast,
      isOnline,
      ______, ________, _________, __________, ___________,
      pageNumber,
    ) => () => {
      const isPersonalFile = documentORM.model.accessLevel === DocumentAccessLevel.USER

      // [TODO-2126] - Use the ORM's latestUsableDocumentVersion instead
      const documentVersion =
        (!isOnline && documentORM.relations.version.cachedDocumentVersionORM) ||
        documentORM.relations.version.latestPublishedDocumentVersionORM ||
        documentORM.relations.version.latestDocumentVersionORM

      dispatch(
        contentPreviewModalActions.setModalVisibility({
          documentVersionId: documentVersion.model.id,
          isFullWindow: false,
          isOpen: true,
          toast: toast,
          content: isPersonalFile ? removeCircularDependencies(documentVersion) : documentVersion,
          pageNumber,
        }),
      )
    },
  },
  publisherPreview: {
    icon: 'play-fill',
    title: DocumentContextActions.publisherPreview,
    onPress: (
      documentORM, dispatch, _user, _match, toast, isOnline,
      _emailTemplates, _onCallback, _actionCallback, _customFields, _downloadBeaconFile,
      pageNumber,
    ) => () => {
      const documentVersion = (!isOnline && documentORM.relations.version.cachedDocumentVersionORM) ||
        documentORM.relations.version.latestDocumentVersionORM

      dispatch(
        contentPreviewModalActions.setModalVisibility({
          documentVersionId: documentVersion.model.id,
          isFullWindow: false,
          isOpen: true,
          toast: toast,
          content: documentVersion,
          pageNumber,
        }),
      )
    },
  },
  download: {
    icon: 'download-simple',
    title: DocumentContextActions.download,
    onPress: (
      documentORM, dispatch, user, _match, _toast, _isOnline, _emailTemplates,
      _onCallback, _actionCallback, _customFields, enablePowerPointAddin, _pageNumber, context,
    ) => () => {
      const currentDocVersion = documentORM.relations.version.latestPublishedDocumentVersionORM ||
        documentORM.relations.version.latestDocumentVersionORM

      analytics?.track('DOCUMENT_DOWNLOAD', {
        action: 'DOWNLOAD',
        category: 'DOCUMENT',
        documentId: documentORM.model.id,
        documentVersionId: currentDocVersion.model.id,
        context,
      })

      const getAddinBeaconFile = (documentVersion: DocumentVersionORM) => {
        if ((documentVersion.model.converterVersion && documentVersion.model.converterVersion < 3) ||
          documentVersion.model.type !== 'PPTX'
        ) {
          return currentDocVersion.meta.srcFileDownloadURL;
        }

        const tenant = documentORM.model.tenantId;
        const documentId = documentVersion.model.documentId;
        const documentVersionId = documentVersion.model.id;

        return `${tenant}/${documentId}/${documentVersionId}/${documentVersionId}_beacon.pptx`;
      }

      const newUrl = enablePowerPointAddin
        ? getAddinBeaconFile(currentDocVersion)
        : currentDocVersion.meta.srcFileDownloadURL

      downloadContentFromCloudfront(
        newUrl,
        currentDocVersion.model.srcFilename!,
        documentORM.model.type,
      )
    },
  },
  version: {
    icon: 'stack',
    title: DocumentContextActions.version,
    onPress: (documentORM, _, __, ___, ____, ______, _______, ________, actionCallback) => () => {
      // [NOTE] - We rely on parent component to do some action to enable the versioning panel
      //          i.e. at a component level
      actionCallback?.(documentORM)
    },
  },
  downloadAsPDF: {
    icon: 'file-pdf-fill',
    title: DocumentContextActions.downloadAsPDF,
    onPress: (
      documentORM, dispatch, _u, _match, toast, _io, _et, _oc, _ac, _cf, _dbf, _pn, context,
    ) => async () => {
      const currentDocVersion = documentORM.relations.version.latestPublishedDocumentVersionORM ||
        documentORM.relations.version.latestDocumentVersionORM

      analytics?.track('DOCUMENT_DOWNLOAD', {
        action: 'DOWNLOAD_AS_PDF',
        category: 'DOCUMENT',
        documentId: documentORM.model.id,
        documentVersionId: currentDocVersion.model.id,
        context,
      })

      logger.util.info('Starting DocVer as PDF download')
      const processingToastId = toast.add(
        <GenericToast
          title="Generating PDF download..."
          status="information"
        />,
        ToastOrientations.TOP_RIGHT,
      )
      try {
        const appsyncClient = generateClient<DownloadDocumentVersionAsPdfFileMutation>();
        const { data } = await appsyncClient.graphql({
          query: downloadDocumentVersionAsPdfFile,
          variables: {
            documentVersionId: currentDocVersion.model.id,
          },
        })
        logger.util.info('Generated download URL -- Starting download')
        toast.add(
          <GenericToast
            title="Download Started..."
            status="download"
          />,
          ToastOrientations.TOP_RIGHT,
          true,
        )
        downloadAsLink(data.downloadDocumentVersionAsPdfFile)
      } catch (e) {
        logger.util.error(e)
        toast.add(
          <GenericToast
            title="There was an error downloading this Document. Please contact support"
            status="error"
          />,
          ToastOrientations.TOP_RIGHT,
          true,
          0,
        )
      } finally {
        toast.remove(processingToastId)
      }
    },
  },
}
